import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { CalendarDays, Eye, Code, Loader2 } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n.js";

// Antes vivían apretadas dentro de la página genérica de Configuración
// (textarea de 3 líneas, sin vista previa, sin ayuda para insertar
// variables) — este tab les da su propio espacio, igual que la pestaña
// "Configuración" de Email Marketing.

const inputClass =
  "w-full text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all";

const TEMPLATES = [
  {
    key: "appointmentTemplateDayBefore",
    label: "Recordatorio del día anterior",
    hint: "Incluye {{link}} para que el contacto pueda confirmar o cancelar.",
  },
  {
    key: "appointmentTemplateSameDay",
    label: "Recordatorio del día de la cita",
    hint: "El bloque {{#link}}...{{/link}} solo aparece si la cita aún no fue confirmada.",
  },
  {
    key: "appointmentTemplateConfirmed",
    label: "Acuse al confirmar",
    hint: "Se envía justo después de que el contacto confirma desde el enlace.",
  },
  {
    key: "appointmentTemplateCancelled",
    label: "Acuse al cancelar",
    hint: "Se envía justo después de que el contacto cancela desde el enlace.",
  },
  {
    key: "appointmentTemplateRescheduled",
    label: "Acuse al reprogramar",
    hint: "Se envía cuando el contacto elige un nuevo horario desde el enlace.",
  },
  {
    key: "appointmentTemplateBooked",
    label: "Cita agendada (auto-reserva)",
    hint: "Se envía cuando el propio contacto agenda su cita — desde la página pública o pidiéndoselo a la IA por WhatsApp.",
  },
];

const VARIABLES = [
  "nombre",
  "fecha",
  "hora",
  "motivo",
  "profesional",
  "ubicacion",
  "duracion",
  "link",
];

const SAMPLE_VALUES = {
  nombre: "Juan Pérez",
  fecha: "lunes 10 de agosto",
  hora: "3:00 PM",
  motivo: "Consulta general",
  profesional: "Dra. García",
  ubicacion: "Consultorio 2",
  duracion: "30 min",
  link: "https://tuapp.com/cita/abc123",
};

// Aproximación de Mustache solo para la vista previa (no hay librería Mustache
// en el frontend, y agregarla solo para esto sería excesivo): las secciones
// {{#tag}}...{{/tag}} se muestran siempre con su contenido — en la vista
// previa se asume que el dato sí está presente — y las variables sueltas se
// sustituyen por un valor de ejemplo.
const renderPreview = (template) => {
  let text = template || "";
  text = text.replace(/\{\{#(\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g, "$2");
  text = text.replace(/\{\{(\w+)\}\}/g, (_, key) => SAMPLE_VALUES[key] ?? "");
  return text;
};

const TemplateEditor = ({ template, value, onSave }) => {
  const [text, setText] = useState(value);
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    setText(value);
  }, [value]);

  const insertVariable = (tag) => {
    const el = textareaRef.current;
    const token = `{{${tag}}}`;
    if (!el) {
      setText((prev) => `${prev}${token}`);
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = `${text.slice(0, start)}${token}${text.slice(end)}`;
    setText(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleBlur = () => {
    if (text !== value) onSave(template.key, text);
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {template.label}
        </p>
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
        >
          {showPreview ? (
            <>
              <Code className="w-3.5 h-3.5" /> Ver texto
            </>
          ) : (
            <>
              <Eye className="w-3.5 h-3.5" /> Vista previa
            </>
          )}
        </button>
      </div>

      {showPreview ? (
        <div className="w-full min-h-[10rem] whitespace-pre-wrap text-sm p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300">
          {renderPreview(text)}
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={handleBlur}
          rows={8}
          className={`${inputClass} resize-y font-mono text-sm`}
        />
      )}

      <div className="flex flex-wrap gap-1 mt-2">
        {VARIABLES.map((tag) => (
          <button
            key={tag}
            type="button"
            onClick={() => insertVariable(tag)}
            title={`Insertar {{${tag}}}`}
            className="px-1.5 py-0.5 text-[11px] font-mono rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 hover:text-indigo-700 dark:hover:text-indigo-400 transition-colors"
          >
            {`{{${tag}}}`}
          </button>
        ))}
      </div>

      <p className="text-[11px] text-slate-400 mt-1.5">{template.hint}</p>
    </div>
  );
};

const AppointmentSettingsTab = ({ canEdit }) => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    };
    load();
  }, []);

  const getSettingValue = (key) => settings.find((s) => s.key === key)?.value || "";

  const saveSetting = async (key, value) => {
    try {
      await api.put(`/settings/${key}`, { value });
      setSettings((prev) => {
        const exists = prev.some((s) => s.key === key);
        return exists
          ? prev.map((s) => (s.key === key ? { ...s, value } : s))
          : [...prev, { key, value }];
      });
      toast.success(i18n.t("settings.success"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleBlurField = (e) => {
    const { name, value } = e.target;
    if (value === getSettingValue(name)) return;
    saveSetting(name, value);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-4">
      {!canEdit && (
        <p className="text-xs text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
          Solo quien administra horarios y motivos puede editar esta configuración.
        </p>
      )}

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
              Recordatorios de citas
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              El recordatorio del día anterior sale a esta hora; el del mismo día también
            </p>
          </div>
        </div>

        <fieldset disabled={!canEdit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
              Hora de envío
            </p>
            <select
              name="appointmentReminderHour"
              value={getSettingValue("appointmentReminderHour")}
              onChange={handleBlurField}
              className={inputClass}
            >
              {Array.from({ length: 24 }, (_, hour) => (
                <option key={hour} value={String(hour)}>
                  {String(hour).padStart(2, "0")}:00
                </option>
              ))}
            </select>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
              Zona horaria (IANA)
            </p>
            <input
              type="text"
              name="appointmentTimezone"
              defaultValue={getSettingValue("appointmentTimezone")}
              onBlur={handleBlurField}
              placeholder="America/Santo_Domingo"
              className={inputClass}
            />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
              Antelación mínima para reprogramar (horas)
            </p>
            <input
              type="number"
              min={0}
              max={168}
              name="appointmentMinLeadHours"
              defaultValue={getSettingValue("appointmentMinLeadHours")}
              onBlur={handleBlurField}
              className={inputClass}
            />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
              Máximo de reprogramaciones por cita
            </p>
            <input
              type="number"
              min={0}
              max={10}
              name="appointmentMaxReschedules"
              defaultValue={getSettingValue("appointmentMaxReschedules")}
              onBlur={handleBlurField}
              className={inputClass}
            />
          </div>
        </fieldset>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-3">
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
          Plantillas de mensaje
        </p>
        <fieldset disabled={!canEdit} className="space-y-3">
          {TEMPLATES.map((template) => (
            <TemplateEditor
              key={template.key}
              template={template}
              value={getSettingValue(template.key)}
              onSave={saveSetting}
            />
          ))}
        </fieldset>
      </div>
    </div>
  );
};

export default AppointmentSettingsTab;
